import React from 'react';
import { InlineLoadingState } from '../../../components/LoadingSpinner';
import type { CustomerNumberingSettings } from '../../../types';
import { AppSelect } from '../../../components/AppSelect';
import {
  DEFAULT_CUSTOMER_NUMBER_FORMAT,
  buildCustomerNumberFormat,
  normalizeCustomerNumberFormat,
  parseCustomerNumberFormatParts,
  renderCustomerNumber,
  type CustomerNumberFormatParts,
  type CustomerNumberYearMode,
} from '../../../utils/customerNumberFormat';
import { SettingsCard } from '../components/SettingsCard';
import { SettingsCardSavedStatus } from '../components/SettingsCardSavedStatus';
import {
  SETTINGS_BTN_PRIMARY,
  SETTINGS_FIELD_LABEL,
  SETTINGS_INPUT_CLASS,
  SETTINGS_SELECT_TRIGGER_CLASS,
} from '../utils/settingsSectionStyles';

const fieldLabel = `block ${SETTINGS_FIELD_LABEL}`;
const hintClass = 'mt-1.5 text-xs text-app-text-subtle leading-relaxed';
const inputClass = SETTINGS_INPUT_CLASS;
const btnPrimary = SETTINGS_BTN_PRIMARY;
const selectTriggerClass = SETTINGS_SELECT_TRIGGER_CLASS;

const YEAR_OPTIONS = [
  { value: 'year', label: 'Ja, volles Jahr (z. B. 2026)' },
  { value: 'yy', label: 'Ja, kurz (z. B. 26)' },
  { value: 'none', label: 'Nein' },
];

/** 1 Stelle ≈ keine führenden Nullen (37 statt 00037). */
const SEQ_DIGIT_OPTIONS = [
  { value: '1', label: 'Ohne führende Nullen (1, 2, 37 …)' },
  { value: '2', label: '2 Stellen mit Nullen (01, 02 …)' },
  { value: '3', label: '3 Stellen mit Nullen (001, 002 …)' },
  { value: '4', label: '4 Stellen mit Nullen (0001 …)' },
  { value: '5', label: '5 Stellen mit Nullen (00001 …)' },
  { value: '6', label: '6 Stellen mit Nullen (000001 …)' },
];

function partsEqual(a: CustomerNumberFormatParts, b: CustomerNumberFormatParts): boolean {
  return a.prefix === b.prefix && a.yearMode === b.yearMode && a.seqDigits === b.seqDigits;
}

function normalizeSeqDigitsForUi(digits: number): number {
  if (digits <= 1) return 1;
  if (digits >= 6) return 6;
  return digits;
}

function parseNextSeqDraft(raw: string): number | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || !Number.isInteger(n) || n < 1 || n > 99999999) return null;
  return n;
}

export function CustomerNumberingSettingsSection() {
  const [loaded, setLoaded] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [parts, setParts] = React.useState<CustomerNumberFormatParts>(() =>
    parseCustomerNumberFormatParts(DEFAULT_CUSTOMER_NUMBER_FORMAT),
  );
  const [baselineParts, setBaselineParts] = React.useState<CustomerNumberFormatParts>(() =>
    parseCustomerNumberFormatParts(DEFAULT_CUSTOMER_NUMBER_FORMAT),
  );
  const [nextSeqDraft, setNextSeqDraft] = React.useState('1');
  const [baselineNextSeq, setBaselineNextSeq] = React.useState(1);
  const [manualEditEnabled, setManualEditEnabled] = React.useState(true);
  const [baselineManualEditEnabled, setBaselineManualEditEnabled] = React.useState(true);
  const [currentYear, setCurrentYear] = React.useState(() => new Date().getFullYear());

  const applyFromApi = React.useCallback((d: CustomerNumberingSettings) => {
    const next = parseCustomerNumberFormatParts(d.customer_number_format);
    next.seqDigits = normalizeSeqDigitsForUi(next.seqDigits);
    setParts(next);
    setBaselineParts(next);
    setNextSeqDraft(String(d.next_seq));
    setBaselineNextSeq(d.next_seq);
    setManualEditEnabled(d.manual_edit_enabled !== false);
    setBaselineManualEditEnabled(d.manual_edit_enabled !== false);
    setCurrentYear(d.current_year);
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    setError(null);
    setSaved(false);
    fetch('/api/settings/customer-numbering', { credentials: 'include' })
      .then(async (r) => {
        if (!r.ok) {
          throw new Error((await r.json().catch(() => null))?.error || 'Einstellungen konnten nicht geladen werden.');
        }
        return r.json() as Promise<CustomerNumberingSettings>;
      })
      .then((d) => {
        if (cancelled) return;
        applyFromApi(d);
        setLoaded(true);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Laden fehlgeschlagen.');
        setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [applyFromApi]);

  const formatDraft = React.useMemo(() => buildCustomerNumberFormat(parts), [parts]);

  const draftValidation = React.useMemo(
    () => normalizeCustomerNumberFormat(formatDraft),
    [formatDraft],
  );

  const parsedNextSeq = React.useMemo(() => parseNextSeqDraft(nextSeqDraft), [nextSeqDraft]);

  const preview = React.useMemo(() => {
    if (draftValidation.ok === false || parsedNextSeq == null) return '—';
    return renderCustomerNumber(draftValidation.format, parsedNextSeq, currentYear);
  }, [draftValidation, parsedNextSeq, currentYear]);

  const dirty =
    !partsEqual(parts, baselineParts) ||
    (parsedNextSeq != null ? parsedNextSeq !== baselineNextSeq : nextSeqDraft.trim() !== String(baselineNextSeq)) ||
    manualEditEnabled !== baselineManualEditEnabled;

  const updateParts = (patch: Partial<CustomerNumberFormatParts>) => {
    setParts((prev) => ({ ...prev, ...patch }));
    setSaved(false);
    setError(null);
  };

  const save = async () => {
    setSaving(true);
    setSaved(false);
    setError(null);
    const format = buildCustomerNumberFormat(parts);
    const normalized = normalizeCustomerNumberFormat(format);
    if (normalized.ok === false) {
      setError(normalized.error);
      setSaving(false);
      return;
    }
    const nextSeq = parseNextSeqDraft(nextSeqDraft);
    if (nextSeq == null) {
      setError('Nächste Nummer muss eine ganze Zahl ab 1 sein.');
      setSaving(false);
      return;
    }
    try {
      const res = await fetch('/api/settings/customer-numbering', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          customer_number_format: normalized.format,
          next_seq: nextSeq,
          manual_edit_enabled: manualEditEnabled,
        }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error((d as { error?: string }).error || 'Speichern fehlgeschlagen');
      applyFromApi(d as CustomerNumberingSettings);
      setSaved(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  return (
    <SettingsCard
      className="min-w-0"
      title="Kundennummern"
      description="Legen Sie fest, wie neue Kundennummern automatisch aussehen. Bestehende Nummern bleiben unverändert. Ob Nummern nachträglich manuell geändert werden dürfen, steuern Sie unten."
    >
        {!loaded ? (
          <InlineLoadingState label="Laden …" className="justify-start" />
        ) : (
          <div className="space-y-5">
            <div>
              <label className={fieldLabel} htmlFor="customer-number-prefix">
                Präfix
              </label>
              <input
                id="customer-number-prefix"
                type="text"
                value={parts.prefix}
                onChange={(e) => updateParts({ prefix: e.target.value })}
                className={`${inputClass} mt-1.5`}
                spellCheck={false}
                autoComplete="off"
                placeholder="z. B. KND oder K"
                maxLength={24}
              />
              <p className={hintClass}>
                Steht am Anfang der Nummer. Leer lassen, wenn nur die laufende Nummer gewünscht ist.
              </p>
            </div>

            <div>
              <label className={fieldLabel} htmlFor="customer-number-year">
                Jahr einbauen?
              </label>
              <div className="mt-1.5">
                <AppSelect
                  id="customer-number-year"
                  value={parts.yearMode}
                  onChange={(v) => updateParts({ yearMode: v as CustomerNumberYearMode })}
                  options={YEAR_OPTIONS}
                  className="w-full"
                  triggerClassName={selectTriggerClass}
                />
              </div>
              <p className={hintClass}>
                Mit Jahr startet die laufende Nummer jedes Kalenderjahr neu. Ohne Jahr läuft sie durchgehend weiter.
              </p>
            </div>

            <div>
              <label className={fieldLabel} htmlFor="customer-number-digits">
                Laufende Nummer
              </label>
              <div className="mt-1.5">
                <AppSelect
                  id="customer-number-digits"
                  value={String(normalizeSeqDigitsForUi(parts.seqDigits))}
                  onChange={(v) => updateParts({ seqDigits: Number(v) || 1 })}
                  options={SEQ_DIGIT_OPTIONS}
                  className="w-full"
                  triggerClassName={selectTriggerClass}
                />
              </div>
              <p className={hintClass}>
                Ohne führende Nullen: <span className="font-mono">K-37</span>. Mit Nullen:{' '}
                <span className="font-mono">K-00037</span>.
              </p>
            </div>

            <div>
              <label className={fieldLabel} htmlFor="customer-number-next-seq">
                Nächste Nummer
              </label>
              <input
                id="customer-number-next-seq"
                type="number"
                min={1}
                step={1}
                value={nextSeqDraft}
                onChange={(e) => {
                  setNextSeqDraft(e.target.value);
                  setSaved(false);
                  setError(null);
                }}
                className={`${inputClass} mt-1.5 tabular-nums`}
              />
              <p className={hintClass}>
                Die nächste automatisch vergebene laufende Nummer — beliebig wählbar, auch niedriger
                als bisherige Nummern. Beispiel: 10 → als Nächstes kommt{' '}
                <span className="font-mono">…-10</span>.
              </p>
            </div>

            <div className="rounded-lg border border-app-border bg-app-surface-muted/60 px-4 py-3">
              <p className={SETTINGS_FIELD_LABEL}>Vorschau</p>
              <p className="mt-1 text-base font-medium text-app-text tabular-nums">{preview}</p>
            </div>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                className="mt-1 rounded border-app-border text-pipedrive-blue focus:ring-pipedrive-blue/30"
                checked={manualEditEnabled}
                onChange={(e) => {
                  setManualEditEnabled(e.target.checked);
                  setSaved(false);
                  setError(null);
                }}
              />
              <span>
                <span className={`block ${SETTINGS_FIELD_LABEL}`}>
                  Manuelle Bearbeitung erlauben
                </span>
                <span className={hintClass}>
                  Wenn deaktiviert, werden Kundennummern nur automatisch vergeben und können in der
                  Detailansicht nicht mehr geändert werden.
                </span>
              </span>
            </label>

            {error ? (
              <div className="p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-200" role="alert">
                {error}
              </div>
            ) : null}

            <div className="flex items-center gap-2 pt-1">
              <button
                type="button"
                onClick={() => void save()}
                disabled={saving || !dirty || draftValidation.ok === false || parsedNextSeq == null}
                className={btnPrimary}
              >
                {saving ? 'Speichern …' : 'Speichern'}
              </button>
              <SettingsCardSavedStatus saved={saved && !error} />
              {dirty && (
                <button
                  type="button"
                  onClick={() => {
                    setParts(baselineParts);
                    setNextSeqDraft(String(baselineNextSeq));
                    setManualEditEnabled(baselineManualEditEnabled);
                    setError(null);
                    setSaved(false);
                  }}
                  disabled={saving}
                  className="inline-flex h-9 items-center px-3 text-sm text-app-text-muted hover:text-app-text"
                >
                  Zurücksetzen
                </button>
              )}
            </div>
          </div>
        )}
    </SettingsCard>
  );
}
